/**
 * A material order that does not show what the messages ask for (ADR 0234,
 * issue #496).
 *
 * The materials-check Edge Function asks Jev, per job, which order line and
 * which note, email or text disagree, and how sure it is, and keeps one row
 * per job in `materials_checks`. This decides what the job page says about
 * that row. It is a heads-up: it never blocks, and anyone can dismiss it
 * (Josh, 2026-09-29).
 *
 * Measured before it was built, on 2026-09-29 against the 38 production jobs
 * with a material order and messages: the four at 60% or more were all real —
 * #1020 Charcoal asked for and Rustic Black ordered, #1021 galvalume asked for
 * and the only line "Falon's estimate", #1022 24 ga asked for and 26 ga
 * ordered, #1028 Hardie siding, trim and a chimney pan asked for and none of
 * them ordered — and no clean job reached it. Two of the four were still
 * drafts, which is why drafts are checked too. Below 90% it says "looks
 * like", the house rule on confidence (`replyCertainty`).
 */

import { replyCertainty } from "./replyReading.js";
import { shortDayMonth } from "./dates.js";

/** Below this Jev's answer says nothing. */
export const MATERIALS_WARN_AT = 0.6;

/** A `materials_checks` row, as far as the heads-up needs it. */
export interface MaterialsCheckRow {
  job_id: string;
  fingerprint: string;
  probability: number | string;
  order_number: number | string | null;
  order_draft: boolean | null;
  line: string | null;
  quote: string | null;
  quote_kind: string | null;
  quote_at: string | null;
  dismissed_fingerprint: string | null;
  /** What the heads-up is about; absent on a row written before ADR 0258. */
  about?: string | null;
  dismissed_about?: string | null;
  /** One line saying what is missing (issue #731); absent before it. */
  summary?: string | null;
}

export interface MaterialsWarning {
  jobId: string;
  /** "Looks like" below 90%. */
  likely: boolean;
  title: string;
  /** "Draft order" or "Placed order". */
  orderLabel: string;
  /** The order line, as Jev was shown it: "MO-900066: 26 ga Kynar panel". */
  line: string;
  /** Where the quote came from and when: "Email, 24 Sep". */
  source: string | null;
  quote: string;
  /**
   * One line saying what was asked for and what the order shows instead:
   * "Asked for Hardie fiber cement siding and trim; the order has only apron
   * flashing." Null when the check wrote none.
   */
  summary: string | null;
}

/** The most of a message the check keeps: `QUOTE_MAX` in materials-check. */
export const MATERIALS_QUOTE_MAX = 300;

/**
 * The quote as shown: a message the check cut at `MATERIALS_QUOTE_MAX` ends at
 * a word, with an ellipsis, rather than mid-word. Done here rather than when
 * the check writes it, because the stored quote is part of what "This is
 * fine" was said about (ADR 0258).
 */
export function materialsQuote(quote: string): string {
  if (quote.length < MATERIALS_QUOTE_MAX) return quote;
  const space = quote.lastIndexOf(" ");
  const kept = space > 0 ? quote.slice(0, space) : quote;
  return `${kept.replace(/[\s,;:.]+$/, "")}…`;
}

const KIND_LABEL: Record<string, string> = {
  email: "Email",
  text: "Text",
  note: "Note",
  call: "Call",
};

/** "Draft order" while it has not gone to the supplier, else "Placed order". */
export function materialsOrderLabel(draft: boolean | null): string {
  return draft ? "Draft order" : "Placed order";
}

/**
 * Whether somebody said "This is fine" about what this row warns of. Held
 * against `about` (the order line and the quoted message, ADR 0258), so new
 * messages elsewhere on the job leave it hidden; a row written before `about`
 * existed falls back to everything Jev was shown.
 */
export function materialsDismissed(row: MaterialsCheckRow): boolean {
  if (row.about) return row.dismissed_about === row.about;
  return row.dismissed_fingerprint === row.fingerprint;
}

/**
 * The heads-up a row raises, or null: no row, under 60%, no pair to show, or
 * somebody said "This is fine" about what it warns of.
 */
export function materialsWarning(
  row: MaterialsCheckRow | null | undefined,
): MaterialsWarning | null {
  if (!row) return null;
  const probability = Number(row.probability);
  if (!Number.isFinite(probability) || probability < MATERIALS_WARN_AT) {
    return null;
  }
  if (!row.line || !row.quote) return null;
  if (materialsDismissed(row)) return null;

  const likely = replyCertainty(probability) !== "stated";
  const when = row.quote_at ? shortDayMonth(row.quote_at) : null;
  const kind = row.quote_kind ? (KIND_LABEL[row.quote_kind] ?? null) : null;
  return {
    jobId: row.job_id,
    likely,
    title: likely
      ? "Looks like the material order doesn’t show what was asked for."
      : "The material order doesn’t show what was asked for.",
    orderLabel: materialsOrderLabel(row.order_draft),
    line: row.line,
    source: [kind, when].filter(Boolean).join(", ") || null,
    quote: materialsQuote(row.quote),
    summary: row.summary?.trim() || null,
  };
}
